// Pure graph projection for an owner's private rolodex. Public discovery paths
// are built separately from network_edges; this module is allowed to see the
// owner's private facet origins and context labels.

import {
  CONTEXT_ALL,
  CONTEXT_PROFESSIONAL,
  facetContextsFor,
  facetsFor,
  filterEntriesByContext,
  identityOf,
  normalizeRolodexEntries,
  originForContext,
  personKeyOf,
  resolveOriginTarget,
  selectActiveFacet,
} from "./rolodexContext";
import { contactProfileForEntry } from "./privateContactProfile";
import { locationForEntry } from "./locationClusters";

function profileOf(entry) {
  const id = personKeyOf(entry);
  const identity = identityOf(entry);
  return {
    id,
    full_name: identity.full_name || entry?.person?.name || "Unknown",
    ...contactProfileForEntry(entry),
  };
}

/** Where the map should seat this person. See lib/locationClusters. */
const locationOf = locationForEntry;

/**
 * Project private entries into a context-aware D3 graph.
 *
 * `context` is personal, professional, or all. Opposite-context origin people
 * are included as muted boundary nodes, never as ordinary members of the view.
 */
export function buildContextGraph(entries, currentUid, context = "all", currentProfile = null) {
  const list = normalizeRolodexEntries(entries);
  const selectedContexts = facetContextsFor(context);
  const visibleEntries = filterEntriesByContext(list, context);

  const nodes = [
    {
      id: currentUid,
      degree: 0,
      profile: currentProfile || { id: currentUid, full_name: "You" },
      contexts: selectedContexts,
    },
  ];
  const nodeById = new Map([[currentUid, nodes[0]]]);

  visibleEntries.forEach((entry) => {
    const id = personKeyOf(entry);
    if (!id || id === currentUid) return;
    const contexts = facetsFor(entry).filter((item) =>
      selectedContexts.includes(item)
    );
    const preferred = contexts.includes(CONTEXT_PROFESSIONAL)
      ? CONTEXT_PROFESSIONAL
      : contexts[0];
    const facet = selectActiveFacet(
      entry,
      context === CONTEXT_ALL ? CONTEXT_ALL : preferred,
      preferred
    ) || {};
    const existing = nodeById.get(id);
    if (existing) {
      existing.contexts = [...new Set([...(existing.contexts || []), ...contexts])];
      return;
    }
    const node = {
      id,
      entryId: entry.id,
      degree: 1,
      profile: profileOf(entry),
      location: locationOf(entry),
      contexts,
      context: contexts.length === 1 ? contexts[0] : "all",
      relationshipType: facet.relationship_type,
      connectionStrength: facet.connection_strength,
      parentUid: currentUid,
      privateEntry: true,
    };
    nodes.push(node);
    nodeById.set(id, node);
  });

  // Pull an opposite-context parent into this private map as a boundary node.
  visibleEntries.forEach((entry) => {
    selectedContexts.forEach((selected) => {
      const origin = originForContext(entry, selected);
      const resolvedOrigin = resolveOriginTarget(origin, list);
      const parentId = resolvedOrigin.personKey;
      if (!parentId || parentId === currentUid || nodeById.has(parentId)) return;
      const parentEntry = resolvedOrigin.entry;
      if (!parentEntry) return;
      const parentContexts = facetsFor(parentEntry);
      const parentNode = {
        id: parentId,
        entryId: parentEntry.id,
        degree: 1,
        profile: profileOf(parentEntry),
        location: locationOf(parentEntry),
        contexts: parentContexts,
        context: parentContexts[0],
        parentUid: currentUid,
        boundary: true,
        privateEntry: true,
      };
      nodes.push(parentNode);
      nodeById.set(parentId, parentNode);
    });
  });

  const rawEdges = [];
  visibleEntries.forEach((entry) => {
    const target = personKeyOf(entry);
    if (!target || target === currentUid) return;
    selectedContexts.forEach((selected) => {
      const facet = selectActiveFacet(entry, selected);
      if (!facet) return;
      const origin = originForContext(entry, selected);
      const resolvedOrigin = resolveOriginTarget(origin, list);
      let source = resolvedOrigin.personKey || currentUid;
      if (!nodeById.has(source) || source === target) source = currentUid;
      rawEdges.push({
        source,
        target,
        context: selected,
        connectionStrength: facet.connection_strength,
        mode: origin?.mode || "direct",
        boundary: Boolean(nodeById.get(source)?.boundary),
        orphanedOrigin: Boolean(
          origin &&
          source === currentUid &&
          resolvedOrigin.personKey !== currentUid
        ),
      });
    });
  });

  // Break malformed or concurrently-created facet cycles at the projection
  // boundary. Editing also excludes descendants, but the graph must remain
  // safe when it encounters old/imported data.
  selectedContexts.forEach((selected) => {
    const parentEdge = new Map(
      rawEdges.filter((edge) => edge.context === selected).map((edge) => [edge.target, edge])
    );
    rawEdges.filter((edge) => edge.context === selected).forEach((edge) => {
      let cursor = edge.source;
      const seen = new Set();
      let cyclic = false;
      while (cursor && cursor !== currentUid && !seen.has(cursor)) {
        if (cursor === edge.target) {
          cyclic = true;
          break;
        }
        seen.add(cursor);
        cursor = parentEdge.get(cursor)?.source;
      }
      if (cyclic) {
        edge.source = currentUid;
        edge.cycleBroken = true;
        edge.boundary = false;
      }
    });
  });

  // Collapse identical personal + professional edges while preserving context.
  const edgesByPair = new Map();
  rawEdges.forEach((edge) => {
    const key = `${edge.source}::${edge.target}`;
    const existing = edgesByPair.get(key);
    if (!existing) {
      edgesByPair.set(key, { ...edge, contexts: [edge.context] });
      return;
    }
    existing.contexts = [...new Set([...existing.contexts, edge.context])];
    if (existing.contexts.length > 1) existing.context = "all";
  });
  const edges = [...edgesByPair.values()];

  // Resolve layout depth without trusting user-authored origin chains.
  const edgeInto = new Map(edges.map((edge) => [edge.target, edge]));
  const depthOf = (id, seen = new Set()) => {
    if (id === currentUid) return 0;
    if (seen.has(id)) return 1;
    seen.add(id);
    const edge = edgeInto.get(id);
    if (!edge || edge.source === id) return 1;
    return Math.min(depthOf(edge.source, seen) + 1, 31);
  };

  nodes.forEach((node) => {
    if (node.id === currentUid) return;
    const edge = edgeInto.get(node.id);
    node.degree = edge?.mode === "sub" ? depthOf(node.id) : 1;
    node.parentUid = edge?.source || currentUid;
    if (edge?.mode === "direct" && edge.source !== currentUid) node.directBranch = true;
    if (edge?.source !== currentUid) node.manualBranch = true;
  });

  return { nodes, edges };
}

/** Backwards-compatible graph/search alias for the canonical person key. */
export const rolodexPersonId = personKeyOf;
